import { useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FolderOpen,
  Settings,
  LogOut,
  PanelLeftClose,
  PanelLeft,
  Activity,
  Users,
  Building2,
  CreditCard,
  Shield,
  ShieldCheck,
  Menu,
  TrendingUp,
  MessageSquareWarning,
} from "lucide-react";
import { requestSeqReportOpen } from "../../lib/seqReport";
import { useToast } from "../ui/ToastModal";
import { useAuth } from "../../hooks/useAuth";
import { useBranding } from "../../hooks/useBranding";
import { useSidebar } from "./SidebarContext";
import { useSafeMode } from "../../contexts/SafeModeContext";
import ShinyText from "../ui/ShinyText";
import {
  isDrawerCollapsed,
  openAfterDrawerCloses,
  SIDEBAR_ID,
  triggerLabel,
} from "./sidebarDrawer";

export function Sidebar() {
  const { profile, signOut, isAdmin, isSuperAdmin, isPlatformAdmin, orgRole } = useAuth();
  const { branding } = useBranding();
  const { isCollapsed, isMobileOpen, toggleSidebar, openMobile, closeMobile } = useSidebar();
  // The phone drawer always renders the full sidebar. A saved desktop collapse
  // (older builds flipped it on every phone tap) must not shrink it to icons.
  const collapsed = isDrawerCollapsed(isCollapsed, isMobileOpen);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const asideRef = useRef<HTMLElement>(null);
  const cancelReportRef = useRef<() => void>(() => {});
  const { isSafeMode, toggleSafeMode } = useSafeMode();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    closeMobile();
    await signOut();
    navigate("/login");
  };

  const handleNavClick = () => {
    closeMobile();
  };

  // Escape closes the phone drawer and hands focus back to the hamburger.
  useEffect(() => {
    if (!isMobileOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      closeMobile();
      triggerRef.current?.focus();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isMobileOpen, closeMobile]);

  // SEQ-192: the widget hides its floating launcher on phones, so this entry
  // (the sidebar is also the phone navigation drawer) is the way in there.
  const { addToast } = useToast();
  // The widget captures the page as it opens, so on a phone the open waits for
  // the drawer to finish sliding out (sidebarDrawer.openAfterDrawerCloses).
  // The widget moves focus into its own form, so nothing refocuses the hamburger.
  const handleReportIssue = () => {
    cancelReportRef.current();
    cancelReportRef.current = openAfterDrawerCloses({
      drawerOpen: isMobileOpen,
      closeDrawer: closeMobile,
      aside: asideRef.current,
      open: () =>
        requestSeqReportOpen(window, () =>
          addToast({
            type: "error",
            title: "Report form unavailable",
            message: "The report form did not load. Check your connection and try again.",
          }),
        ),
    });
  };
  useEffect(() => () => cancelReportRef.current(), []);

  // ── Main Navigation (top) ──────────────────────────
  // SEQ-180: P&L Analytics is shown only with can_view_financials, the same
  // flag that gates the Safe Mode toggle below and the route itself
  // (src/App.tsx, RoleProtectedWrapper).
  const mainNavItems = [
    { to: "/", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/projects", icon: FolderOpen, label: "Projects" },
    ...(profile?.can_view_financials
      ? [{ to: "/pnl-analytics", icon: TrendingUp, label: "P&L Analytics" }]
      : []),
    { to: "/activity", icon: Activity, label: "Activity" },
  ];

  // ── Bottom Navigation (settings area) ──────────────
  const bottomNavItems: { to: string; icon: typeof Settings; label: string; show: boolean }[] = [
    { to: "/team", icon: Users, label: "Team", show: isAdmin || isSuperAdmin },
    { to: "/org-settings", icon: Building2, label: "Organization", show: isAdmin || isSuperAdmin },
    { to: "/settings", icon: Settings, label: "Settings", show: isAdmin || isSuperAdmin },
    { to: "/billing", icon: CreditCard, label: "Billing", show: isSuperAdmin },
    { to: "/super-admin/orgs", icon: Shield, label: "Platform Admin", show: isPlatformAdmin },
  ];

  const visibleBottomNav = bottomNavItems.filter((item) => item.show);

  // Role display
  const roleLabel =
    orgRole === "super_admin" ? "Super Admin" : orgRole === "admin" || isAdmin ? "Administrator" : "User";

  return (
    <>
      {/* Mobile hamburger trigger */}
      <button
        ref={triggerRef}
        type="button"
        className="sidebar-mobile-trigger"
        onClick={isMobileOpen ? closeMobile : openMobile}
        aria-label={triggerLabel(isMobileOpen)}
        aria-expanded={isMobileOpen}
        aria-controls={SIDEBAR_ID}
      >
        <Menu size={20} />
      </button>

      {/* Sidebar */}
      <aside
        id={SIDEBAR_ID}
        ref={asideRef}
        className={[
          "sidebar",
          collapsed ? "collapsed" : "",
          isMobileOpen ? "mobile-open" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {/* ── Header: Logo + Toggle ── */}
        <div className="sidebar-header">
          <div className="sidebar-brand" onClick={() => { navigate("/"); handleNavClick(); }}>
            <div className="sidebar-logo-wrapper">
              <img
                src={collapsed
                  ? (branding.logo_mini_url || branding.logo_url || "/claims-app-logo-mini.svg")
                  : (branding.logo_url || "/claims-app-logo.svg")}
                alt={branding.company_name || branding.app_name}
                className={collapsed ? "sidebar-logo-mini" : "sidebar-logo-full"}
              />
            </div>
            {!collapsed && (
              <div className="sidebar-brand-text">
                <ShinyText 
                  text={branding.app_name || "Claims App"} 
                  className="sidebar-brand-name" 
                  speed={3} 
                  color="rgba(255, 255, 255, 0.85)" 
                  shineColor="#ffffff" 
                />
                <span className="sidebar-brand-company">Streamlined claim management</span>
              </div>
            )}
          </div>
          <button
            className="sidebar-toggle"
            onClick={toggleSidebar}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
          </button>
        </div>

        {/* ── Main Navigation ── */}
        <nav className="sidebar-nav sidebar-nav-main">
          {mainNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `sidebar-nav-item ${isActive ? "active" : ""}`
              }
              onClick={handleNavClick}
              title={collapsed ? item.label : undefined}
            >
              <item.icon size={20} className="sidebar-nav-icon" />
              {!collapsed && <span className="sidebar-nav-label">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* ── Bottom Section: Settings + User ── */}
        <div className="sidebar-bottom">
          {/* Divider */}
          <div className="sidebar-divider" />

          {/* Bottom nav items */}
          <nav className="sidebar-nav sidebar-nav-bottom">
            {visibleBottomNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `sidebar-nav-item ${isActive ? "active" : ""}`
                }
                onClick={handleNavClick}
                title={collapsed ? item.label : undefined}
              >
                <item.icon size={20} className="sidebar-nav-icon" />
                {!collapsed && <span className="sidebar-nav-label">{item.label}</span>}
              </NavLink>
            ))}
          </nav>

          {/* Safe Mode toggle: only for users with financial access */}
          {profile?.can_view_financials && (
            <>
              <div className="sidebar-divider" style={{ margin: '4px 12px' }} />
              <button
                className={`sidebar-nav-item sidebar-safe-mode ${isSafeMode ? 'active' : ''}`}
                onClick={toggleSafeMode}
                title={collapsed
                  ? (isSafeMode ? 'Safe Mode ON. Click to disable (⌘⇧S)' : 'Enable Safe Mode (⌘⇧S)')
                  : undefined}
                aria-pressed={isSafeMode}
                data-tour-id="sidebar-safe-mode-toggle"
              >
                <ShieldCheck
                  size={20}
                  className={`sidebar-nav-icon ${isSafeMode ? 'safe-mode-icon-active' : ''}`}
                />
                {!collapsed && (
                  <span className="sidebar-nav-label">
                    Safe Mode
                    {isSafeMode && (
                      <span className="safe-mode-badge">ON</span>
                    )}
                  </span>
                )}
              </button>
            </>
          )}

          {/* Report an issue (SEQ-192): the only way into the widget, at every width */}
          <button
            type="button"
            className="sidebar-nav-item"
            onClick={handleReportIssue}
            aria-label="Report an issue"
            title={collapsed ? "Report an issue" : undefined}
          >
            <MessageSquareWarning size={20} className="sidebar-nav-icon" />
            {!collapsed && <span className="sidebar-nav-label">Report an issue</span>}
          </button>

          {/* User info */}
          {!collapsed && (
            <div className="sidebar-user-info">
              <div className="sidebar-user-avatar">
                {(profile?.full_name || profile?.email || "U")[0].toUpperCase()}
              </div>
              <div className="sidebar-user-details">
                <p className="sidebar-user-name">
                  {profile?.full_name || profile?.email}
                </p>
                <p className="sidebar-user-role">
                  {roleLabel}
                  {isPlatformAdmin && " · Platform"}
                </p>
              </div>
            </div>
          )}

          {/* Sign out */}
          <button
            className="sidebar-nav-item sidebar-signout"
            onClick={handleSignOut}
            title={collapsed ? "Sign Out" : undefined}
          >
            <LogOut size={20} className="sidebar-nav-icon" />
            {!collapsed && <span className="sidebar-nav-label">Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Mobile overlay backdrop */}
      {isMobileOpen && (
        <div className="sidebar-mobile-overlay" onClick={closeMobile} />
      )}
    </>
  );
}
